// 교관 「전직」 구역의 내용 모델(3개월 C10-5, 순수 로직·DOM 없음). 데이터·판정은 shared/content/jobQuest.ts, 상태는 view.job이다.
// 단계: gates1(관문 3개, Lv30) → pick(갈래 선택) → gates2(관문 3개, Lv40) → advance(승급) → done(전직 완료).
import { JOB_BRANCHES, JOB_LEVEL, jobGatesOf, type JobBranchDef, type JobGateDef, type JobHeroId, type JobTier } from '../shared/content/jobQuest';
import type { JobView } from '../shared/content/jobQuest';

export type JobPhase = 'gates1' | 'pick' | 'gates2' | 'advance' | 'done';
export type JobGateState = 'passed' | 'open' | 'locked';

export interface JobGateRow {
  readonly id: string;
  readonly name: string;
  readonly hint: string;
  readonly line: string;
  readonly state: JobGateState;
  /** 잠김 사유(잠김이 아니면 '') */
  readonly lock: string;
}

export interface JobPanelModel {
  readonly phase: JobPhase;
  /** 지금 보여 줄 관문 단계(done이면 2) */
  readonly gateTier: JobTier;
  readonly gates: readonly JobGateRow[];
  /** 교관 대사(첫 관문의 line 대신 단계 안내) */
  readonly say: string;
  /** 갈래/승급 버튼을 눌러도 되는 레벨인가 */
  readonly levelOk: boolean;
  readonly needLevel: number;
  /** 전직한 갈래(전직 전이면 null) */
  readonly branch: JobBranchDef | null;
}

/** 관문 한 줄: 통과 ✓ / 열림 / 잠김(사유) */
export function jobGateRow(g: JobGateDef, view: Pick<JobView, 'passed' | 'open'>, level: number): JobGateRow {
  const passed = view.passed.includes(g.id), open = view.open.includes(g.id);
  let lock = '';
  if (!passed && !open) {
    const need = JOB_LEVEL[g.tier];
    lock = level < need ? 'Lv' + need + ' 필요' : '앞 관문을 먼저 넘어야 해요';
  }
  return { id: g.id, name: g.name, hint: g.hint, line: g.line, state: passed ? 'passed' : open ? 'open' : 'locked', lock };
}

/** 교관 대사(관문 단계는 관문 line, 그 밖 단계는 영웅별 안내) */
const SAY: Readonly<Record<JobHeroId, { readonly pick: string; readonly advance: string; readonly done: string }>> = {
  kael: { pick: '관문을 모두 넘었구나. 이제 네 갈래를 정해라. 한번 정하면 되돌릴 수 없다.', advance: '두 번째 각인을 새길 때가 됐다. 준비되면 말해라.', done: '네 길은 이미 열렸다. 검을 믿고 나아가라.' },
  lyra: { pick: '관문을 모두 넘었구나. 이제 네 활이 갈 길을 정해라. 한번 정하면 되돌릴 수 없다.', advance: '활에 두 번째 각인을 새길 때가 됐다. 준비되면 말해라.', done: '네 길은 이미 열렸다. 바람을 읽고 시위를 믿어라.' },
};

export function jobPanelModel(view: JobView, level: number): JobPanelModel {
  const tier = view.tier, ready = view.ready, hero: JobHeroId = view.hero ?? 'kael';
  const phase: JobPhase = tier >= 2 ? 'done' : tier === 1 ? (ready === 2 ? 'advance' : 'gates2') : ready === 1 ? 'pick' : 'gates1';
  const gateTier: JobTier = phase === 'gates2' || phase === 'advance' || phase === 'done' ? 2 : 1;
  const gates = jobGatesOf(hero, gateTier).map((g) => jobGateRow(g, view, level));
  const needLevel = JOB_LEVEL[phase === 'pick' || phase === 'gates1' ? 1 : 2];
  const branches = JOB_BRANCHES[hero], line = SAY[hero];
  const say = phase === 'gates1' ? (gates.find((r) => r.state !== 'passed')?.line ?? gates[0].line)
    : phase === 'pick' ? line.pick
    : phase === 'gates2' ? (gates.find((r) => r.state !== 'passed')?.line ?? gates[0].line)
    : phase === 'advance' ? line.advance
    : line.done;
  return { phase, gateTier, gates, say, levelOk: level >= needLevel, needLevel, branch: tier >= 1 ? branches[view.branch === 1 ? 1 : 0] : null };
}

/** 지금 열려 있어 도전할 수 있는 첫 관문 id(없으면 '') */
export function firstOpenGate(m: JobPanelModel): string {
  return m.gates.find((r) => r.state === 'open')?.id ?? '';
}

/** 전직 연출 문구(순수): 큰 글자(갈래 이름) / 부제(전직 단계 · 새 스킬 습득) / 스킬 두 줄(습득·다음 단계 예고) */
export interface JobCutsceneLines {
  readonly color: string;
  readonly title: string;
  readonly sub: string;
  readonly skills: readonly [{ readonly name: string; readonly note: string; readonly learned: boolean }, { readonly name: string; readonly note: string; readonly learned: boolean }];
}
/** hero = 전직한 영웅(카엘·라이라, 생략 = 카엘) */
export function jobCutsceneLines(tier: number, branch: number, hero: JobHeroId = 'kael'): JobCutsceneLines | null {
  if (tier !== 1 && tier !== 2) return null;
  const b = (JOB_BRANCHES[hero] ?? JOB_BRANCHES.kael)[branch === 1 ? 1 : 0];
  const got = tier === 1 ? b.skills[0] : b.skills[1];
  return {
    color: b.color, title: b.name,
    sub: (tier === 1 ? '전직 I' : '전직 II') + ' · ' + got + ' 습득',
    skills: [
      { name: b.skills[0], note: '전직 I', learned: true },
      { name: b.skills[1], note: tier === 2 ? '전직 II' : 'Lv' + JOB_LEVEL[2] + ' 전직 II에서 습득', learned: tier === 2 },
    ],
  };
}
